import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  FiDollarSign, 
  FiAlertCircle, 
  FiCheckCircle, 
  FiClock, 
  FiPlus, 
  FiEdit, 
  FiTrash2,
  FiFilter 
} from 'react-icons/fi';
import './ContasPagar.css';

const ContasPagar = () => {
  const { userData } = useAuth();
  const navigate = useNavigate();
  
  // Permission check
  const hasPermission = userData?.permissions?.includes('Master') || 
                       userData?.permissions?.includes('Diretoria') ||
                       userData?.permissions?.includes('Financeiro');

  // State
  const [loading, setLoading] = useState(true);
  const [contas, setContas] = useState([]);
  const [summary, setSummary] = useState({});
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({
    status: '',
    categoria: '',
    fornecedor: '',
    startDate: '',
    endDate: ''
  });
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'payment'
  const [selectedConta, setSelectedConta] = useState(null);
  const [formData, setFormData] = useState({
    descricao: '',
    fornecedor: '',
    categoria: '',
    valor: '',
    dataVencimento: '',
    observacoes: ''
  });
  const [paymentForm, setPaymentForm] = useState({
    dataPagamento: new Date().toISOString().split('T')[0],
    formaPagamento: '',
    observacoes: ''
  });
  const [currentPage, setCurrentPage] = useState(1);

  const categorias = ['Aluguel', 'Salários', 'Impostos', 'Fornecedores', 'Serviços', 'Outros'];
  const formasPagamento = ['Boleto', 'Transferência', 'PIX', 'Cheque', 'Dinheiro', 'Cartão'];

  // Currency formatter
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  // Date formatter
  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  // Fetch contas a pagar
  const fetchContasPagar = async () => {
    try {
      setLoading(true);
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_VERCEL_API_URL 
        : 'http://localhost:5000';
      
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: 20,
        ...filters
      });

      const response = await fetch(
        `${API_URL}/api/contas-pagar?${queryParams}`,
        { 
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Erro ao carregar contas a pagar');
      }

      const data = await response.json();
      setContas(data.data.contas);
      setSummary(data.data.summary);
      setPagination(data.data.pagination);
    } catch (error) {
      console.error('Error fetching contas pagar:', error);
      alert('Erro ao carregar dados: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Create conta
  const handleCreate = async () => {
    try {
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_VERCEL_API_URL 
        : 'http://localhost:5000';

      const response = await fetch(
        `${API_URL}/api/contas-pagar`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...formData,
            valor: parseFloat(formData.valor)
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao criar conta');
      }

      alert('Conta criada com sucesso!');
      setShowModal(false);
      resetForm();
      fetchContasPagar();
    } catch (error) {
      console.error('Error creating conta:', error);
      alert('Erro ao criar conta: ' + error.message);
    }
  };

  // Update conta
  const handleUpdate = async () => {
    try {
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_VERCEL_API_URL 
        : 'http://localhost:5000';

      const response = await fetch(
        `${API_URL}/api/contas-pagar/${selectedConta._id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...formData,
            valor: parseFloat(formData.valor)
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao atualizar conta');
      }

      alert('Conta atualizada com sucesso!');
      setShowModal(false);
      resetForm();
      fetchContasPagar();
    } catch (error) {
      console.error('Error updating conta:', error);
      alert('Erro ao atualizar conta: ' + error.message);
    }
  };

  // Register payment
  const handlePayment = async () => {
    try {
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_VERCEL_API_URL 
        : 'http://localhost:5000';

      const response = await fetch(
        `${API_URL}/api/contas-pagar/${selectedConta._id}/payment`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(paymentForm)
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao registrar pagamento');
      }

      alert('Pagamento registrado com sucesso!');
      setShowModal(false);
      resetForm();
      fetchContasPagar();
    } catch (error) {
      console.error('Error registering payment:', error);
      alert('Erro ao registrar pagamento: ' + error.message);
    }
  };

  // Delete conta
  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta conta?')) {
      return;
    }

    try {
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_VERCEL_API_URL 
        : 'http://localhost:5000';

      const response = await fetch(
        `${API_URL}/api/contas-pagar/${id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao excluir conta');
      }

      alert('Conta excluída com sucesso!');
      fetchContasPagar();
    } catch (error) {
      console.error('Error deleting conta:', error);
      alert('Erro ao excluir conta: ' + error.message);
    }
  };

  // Open modal
  const openModal = (mode, conta = null) => {
    setModalMode(mode);
    setSelectedConta(conta);

    if (mode === 'edit' && conta) {
      setFormData({
        descricao: conta.descricao,
        fornecedor: conta.fornecedor,
        categoria: conta.categoria,
        valor: conta.valor.toString(),
        dataVencimento: conta.dataVencimento.split('T')[0],
        observacoes: conta.observacoes || ''
      });
    } else if (mode === 'payment' && conta) {
      setPaymentForm({
        dataPagamento: new Date().toISOString().split('T')[0],
        formaPagamento: '',
        observacoes: ''
      });
    }

    setShowModal(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      descricao: '',
      fornecedor: '',
      categoria: '',
      valor: '',
      dataVencimento: '',
      observacoes: ''
    });
    setPaymentForm({
      dataPagamento: new Date().toISOString().split('T')[0],
      formaPagamento: '',
      observacoes: ''
    });
    setSelectedConta(null);
  };

  useEffect(() => {
    if (!hasPermission) {
      alert('Você não tem permissão para acessar esta página.');
      navigate('/painel');
      return;
    }
    
    fetchContasPagar();
  }, [currentPage, filters, hasPermission, navigate]);

  if (!hasPermission) {
    return null;
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="contas-pagar">
      {/* Header */}
      <div className="page-header">
        <h2>Contas a Pagar</h2>
        <div className="header-actions">
          <button className="add-button" onClick={() => openModal('create')}>
            <FiPlus /> Nova Conta
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-icon">
            <FiDollarSign />
          </div>
          <div className="summary-content">
            <h4>Total Pendente</h4>
            <p className="summary-value">{formatCurrency(summary.totalPendente)}</p>
            <span className="summary-detail">{summary.countPendentes} contas</span>
          </div>
        </div>

        <div className="summary-card vencidas">
          <div className="summary-icon">
            <FiAlertCircle />
          </div>
          <div className="summary-content">
            <h4>Vencidas</h4>
            <p className="summary-value">{formatCurrency(summary.totalVencido)}</p>
            <span className="summary-detail">{summary.countVencidas} contas</span>
          </div>
        </div>

        <div className="summary-card pagas">
          <div className="summary-icon">
            <FiCheckCircle />
          </div>
          <div className="summary-content">
            <h4>Pagas no Período</h4>
            <p className="summary-value">{formatCurrency(summary.totalPago)}</p>
            <span className="summary-detail">{summary.count - summary.countPendentes - summary.countVencidas} contas</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Status:</label>
          <select 
            value={filters.status} 
            onChange={(e) => setFilters({...filters, status: e.target.value})}
          >
            <option value="">Todos</option>
            <option value="Pendente">Pendente</option>
            <option value="Vencido">Vencido</option>
            <option value="Pago">Pago</option>
            <option value="Cancelado">Cancelado</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Categoria:</label>
          <select 
            value={filters.categoria} 
            onChange={(e) => setFilters({...filters, categoria: e.target.value})}
          >
            <option value="">Todas</option>
            {categorias.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Fornecedor:</label>
          <input 
            type="text" 
            value={filters.fornecedor}
            onChange={(e) => setFilters({...filters, fornecedor: e.target.value})}
            placeholder="Buscar fornecedor..."
          />
        </div>

        <div className="filter-group">
          <label>Data Inicial:</label>
          <input 
            type="date" 
            value={filters.startDate}
            onChange={(e) => setFilters({...filters, startDate: e.target.value})}
          />
        </div>

        <div className="filter-group">
          <label>Data Final:</label>
          <input 
            type="date" 
            value={filters.endDate}
            onChange={(e) => setFilters({...filters, endDate: e.target.value})}
          />
        </div>

        <button 
          className="clear-filters-button"
          onClick={() => setFilters({ status: '', categoria: '', fornecedor: '', startDate: '', endDate: '' })}
        >
          Limpar Filtros
        </button>
      </div>

      {/* Table */}
      <div className="table-container">
        <table className="contas-table">
          <thead>
            <tr>
              <th>Descrição</th>
              <th>Fornecedor</th>
              <th>Categoria</th>
              <th>Valor</th>
              <th>Vencimento</th>
              <th>Status</th>
              <th>Forma Pagamento</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {contas.map(conta => (
              <tr key={conta._id} className={conta.status === 'Vencido' ? 'vencida' : ''}>
                <td>{conta.descricao}</td>
                <td>{conta.fornecedor}</td>
                <td>{conta.categoria}</td>
                <td>{formatCurrency(conta.valor)}</td>
                <td>{formatDate(conta.dataVencimento)}</td>
                <td>
                  <span className={`status-badge ${conta.status.toLowerCase()}`}>
                    {conta.status}
                  </span>
                </td>
                <td>{conta.formaPagamento || '-'}</td>
                <td>
                  <div className="action-buttons">
                    {conta.status !== 'Pago' && (
                      <>
                        <button 
                          className="action-button pay"
                          onClick={() => openModal('payment', conta)}
                          title="Registrar Pagamento"
                        >
                          <FiCheckCircle />
                        </button>
                        <button 
                          className="action-button edit"
                          onClick={() => openModal('edit', conta)}
                          title="Editar"
                        >
                          <FiEdit />
                        </button>
                        <button 
                          className="action-button delete"
                          onClick={() => handleDelete(conta._id)}
                          title="Excluir"
                        >
                          <FiTrash2 />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {contas.length === 0 && (
          <div className="empty-state">
            <p>Nenhuma conta a pagar encontrada.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Anterior
          </button>
          <span>Página {pagination.currentPage} de {pagination.totalPages}</span>
          <button 
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === pagination.totalPages}
          >
            Próxima
          </button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>
              {modalMode === 'create' && 'Nova Conta a Pagar'}
              {modalMode === 'edit' && 'Editar Conta a Pagar'}
              {modalMode === 'payment' && 'Registrar Pagamento'}
            </h3>
            
            {(modalMode === 'create' || modalMode === 'edit') && (
              <>
                <div className="form-group">
                  <label>Descrição:</label>
                  <input 
                    type="text" 
                    value={formData.descricao}
                    onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                    placeholder="Descrição da conta"
                  />
                </div>

                <div className="form-group">
                  <label>Fornecedor:</label>
                  <input 
                    type="text" 
                    value={formData.fornecedor}
                    onChange={(e) => setFormData({...formData, fornecedor: e.target.value})}
                    placeholder="Nome do fornecedor"
                  />
                </div>

                <div className="form-group">
                  <label>Categoria:</label>
                  <select 
                    value={formData.categoria}
                    onChange={(e) => setFormData({...formData, categoria: e.target.value})}
                  >
                    <option value="">Selecione uma categoria</option>
                    {categorias.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Valor:</label>
                  <input 
                    type="number" 
                    step="0.01"
                    value={formData.valor}
                    onChange={(e) => setFormData({...formData, valor: e.target.value})}
                    placeholder="0.00"
                  />
                </div>

                <div className="form-group">
                  <label>Data de Vencimento:</label>
                  <input 
                    type="date" 
                    value={formData.dataVencimento}
                    onChange={(e) => setFormData({...formData, dataVencimento: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label>Observações:</label>
                  <textarea 
                    value={formData.observacoes}
                    onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                    rows="3"
                    placeholder="Observações adicionais..."
                  />
                </div>
              </>
            )}

            {modalMode === 'payment' && selectedConta && (
              <>
                <div className="modal-info">
                  <p><strong>Descrição:</strong> {selectedConta.descricao}</p>
                  <p><strong>Fornecedor:</strong> {selectedConta.fornecedor}</p>
                  <p><strong>Valor:</strong> {formatCurrency(selectedConta.valor)}</p>
                </div>

                <div className="form-group">
                  <label>Data do Pagamento:</label>
                  <input 
                    type="date" 
                    value={paymentForm.dataPagamento}
                    onChange={(e) => setPaymentForm({...paymentForm, dataPagamento: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label>Forma de Pagamento:</label>
                  <select 
                    value={paymentForm.formaPagamento}
                    onChange={(e) => setPaymentForm({...paymentForm, formaPagamento: e.target.value})}
                  >
                    <option value="">Selecione</option>
                    {formasPagamento.map(forma => (
                      <option key={forma} value={forma}>{forma}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Observações:</label>
                  <textarea 
                    value={paymentForm.observacoes}
                    onChange={(e) => setPaymentForm({...paymentForm, observacoes: e.target.value})}
                    rows="3"
                    placeholder="Observações sobre o pagamento..."
                  />
                </div>
              </>
            )}

            <div className="modal-actions">
              <button className="cancel-button" onClick={() => setShowModal(false)}>
                Cancelar
              </button>
              <button 
                className="submit-button" 
                onClick={() => {
                  if (modalMode === 'create') handleCreate();
                  else if (modalMode === 'edit') handleUpdate();
                  else if (modalMode === 'payment') handlePayment();
                }}
                disabled={
                  (modalMode === 'payment' && !paymentForm.formaPagamento) ||
                  ((modalMode === 'create' || modalMode === 'edit') && 
                    (!formData.descricao || !formData.fornecedor || !formData.categoria || !formData.valor || !formData.dataVencimento))
                }
              >
                {modalMode === 'create' && 'Criar Conta'}
                {modalMode === 'edit' && 'Salvar Alterações'}
                {modalMode === 'payment' && 'Registrar Pagamento'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContasPagar;
